import type { GroupJournal } from '@shared/types/api'
import { formatRate, formatSessionDate, statusColor, statusLabel } from '@shared/utils/attendance'

interface AttendanceJournalProps {
  journal?: GroupJournal
  isLoading?: boolean
}

/**
 * Журнал посещаемости группы: занятия по горизонтали, спортсмены по вертикали.
 *
 * На десктопе помещается целиком, на телефоне прокручивается вбок — колонка
 * с именами при этом остаётся на месте, иначе непонятно, чья это строка.
 */
export function AttendanceJournal({ journal, isLoading = false }: AttendanceJournalProps) {
  if (isLoading) {
    return (
      <p className="py-8 text-center text-[15px]" style={{ color: 'var(--text-hint)' }}>
        Собираем журнал…
      </p>
    )
  }

  if (!journal || journal.rows.length === 0) {
    return (
      <p className="py-8 text-center text-[15px]" style={{ color: 'var(--text-hint)' }}>
        В группе пока нет спортсменов
      </p>
    )
  }

  if (journal.sessions.length === 0) {
    return (
      <p className="py-8 text-center text-[15px]" style={{ color: 'var(--text-hint)' }}>
        За этот период занятий не было
      </p>
    )
  }

  return (
    <div className="overflow-x-auto" data-testid="attendance-journal">
      <table className="w-full border-collapse text-[13px]">
        <thead>
          <tr>
            <th
              className="sticky left-0 z-10 px-2 py-2 text-left font-normal"
              style={{ backgroundColor: 'var(--bg-primary)', color: 'var(--text-hint)' }}
            >
              Спортсмен
            </th>
            {journal.sessions.map((session) => (
              <th
                key={session.session_id}
                className="px-2 py-2 font-normal"
                style={{
                  color: session.cancelled ? 'var(--text-hint)' : 'var(--text-secondary)',
                  textDecoration: session.cancelled ? 'line-through' : 'none',
                  whiteSpace: 'nowrap',
                }}
                title={session.cancelled ? 'Занятие отменено' : undefined}
              >
                {formatSessionDate(session.date)}
              </th>
            ))}
            <th className="px-2 py-2 font-normal" style={{ color: 'var(--text-hint)' }}>
              Итог
            </th>
          </tr>
        </thead>

        <tbody>
          {journal.rows.map((row) => (
            <tr key={row.athlete_id} style={{ borderTop: '1px solid var(--separator)' }}>
              <td
                className="sticky left-0 z-10 px-2 py-2 text-[15px]"
                style={{ backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)', whiteSpace: 'nowrap' }}
              >
                {row.name}
              </td>

              {journal.sessions.map((session) => {
                const status = row.marks[session.session_id]
                return (
                  <td key={session.session_id} className="px-2 py-2 text-center">
                    <span
                      title={status ? statusLabel(status) : 'без отметки'}
                      aria-label={status ? statusLabel(status) : 'без отметки'}
                      style={{
                        display: 'inline-block',
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: status ? statusColor(status) : 'transparent',
                        border: status ? 'none' : '1px solid var(--separator)',
                      }}
                    />
                  </td>
                )
              })}

              <td
                className="px-2 py-2 text-center text-[13px]"
                style={{ color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}
              >
                {row.marked_count > 0 ? formatRate(row.attendance_rate) : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
